import React, { Component, type ErrorInfo, type ReactNode } from "react"
import { captureReactException, initSentryReact } from "~/lib/sentry"
import { Button } from "../ui/button"
import { Icon } from "../ui/icon"

interface Props {
  children: ReactNode
  componentName?: string
  fallback?: ReactNode | ((error: Error, resetError: () => void) => ReactNode)
}

interface State {
  hasError: boolean
  error: Error | null
}

/**
 * Error Boundary component for React components mounted via Turbo Mount.
 *
 * This component catches JavaScript errors anywhere in its child component tree,
 * logs those errors to Sentry, and displays a fallback UI instead of crashing.
 *
 * Usage:
 * ```tsx
 * <SentryErrorBoundary componentName="MyComponent">
 *   <MyComponent />
 * </SentryErrorBoundary>
 * ```
 */
export class SentryErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props)
    this.state = { hasError: false, error: null }

    // Initialize Sentry when the first error boundary is created
    initSentryReact()
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error }
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    const { componentName } = this.props

    // Capture the error with Sentry
    captureReactException(error, componentName, {
      componentStack: errorInfo.componentStack,
      componentName,
    })

    // Also log to console for development debugging
    console.error(
      `[SentryErrorBoundary] Error in ${componentName || "unknown component"}:`,
      error,
      errorInfo,
    )
  }

  resetError = (): void => {
    this.setState({ hasError: false, error: null })
  }

  render(): ReactNode {
    const { hasError, error } = this.state
    const { children, fallback } = this.props

    if (hasError && error) {
      // If a custom fallback is provided, use it
      if (fallback) {
        if (typeof fallback === "function") {
          return fallback(error, this.resetError)
        }
        return fallback
      }

      // Default fallback UI
      return (
        <div className="sentry-error-boundary-fallback">
          <div className="sentry-error-boundary-fallback__content">
            <Icon icon="exclamation-triangle" size="xl" />
            <p className="mt-2 sentry-error-boundary-fallback__message">
              Something went wrong.
            </p>
            <Button
              variant="primary"
              onClick={this.resetError}
              className="mt-2"
            >
              Try again
            </Button>
          </div>
        </div>
      )
    }

    return children
  }
}

/**
 * Higher-order component that wraps a component with SentryErrorBoundary.
 * This is designed to be used with Turbo Mount's registerComponent.
 *
 * Usage:
 * ```tsx
 * import { withSentryErrorBoundary } from "~/react/components/sentry-error-boundary"
 *
 * const MyComponentWithErrorBoundary = withSentryErrorBoundary(MyComponent, "MyComponent")
 * registerComponent(turboMount, "MyComponent", MyComponentWithErrorBoundary)
 * ```
 */
export function withSentryErrorBoundary<P extends object>(
  WrappedComponent: React.ComponentType<P>,
  componentName: string,
  fallback?: Props["fallback"],
): React.FC<P> {
  const WithErrorBoundary: React.FC<P> = (props) => {
    // Initialize Sentry when using the HOC
    React.useEffect(() => {
      initSentryReact()
    }, [])

    return (
      <SentryErrorBoundary componentName={componentName} fallback={fallback}>
        <WrappedComponent {...props} />
      </SentryErrorBoundary>
    )
  }

  WithErrorBoundary.displayName = `WithSentryErrorBoundary(${componentName})`

  return WithErrorBoundary
}

export default SentryErrorBoundary
